import { calculateCartTotalPrice } from "./calculateCartItemPrice"
import { type Cart } from "../zod/cartItemState"
import { type MenuRoot } from "../zod/menuRootSchema"
import { type TaxSettings } from "../zod/taxSchema"
import { type OrderType } from "../types/orderType"
import { getBestDeliveryZoneWithTaxDetails } from "./getBestDeliveryZone"
import type { Point, DeliveryZone } from "@lib/zod/deliverySchema";
import { calculateFeeTax } from "./calculateFeeTax"
import type { CustomServiceFee } from "../zod/serviceFee";
import { exclusiveRate } from "./rateCalc"
import { fixMath } from "./fixMath";

type CalculatedCustomServiceFee = {
    id: string,
    isEditable: boolean,
    name: string,
    itemTotal: number,
    tax: number,
    totalWithTax: number,
    shownFee: number
};


export type SubTotalResult = {
    itemTotal: number,
    tax: number,
    totalWithTax: number,
    shownFee: number,
    calculatedCustomServiceFees: CalculatedCustomServiceFee[],
    bestDeliveryZone: ReturnType<typeof getBestDeliveryZoneWithTaxDetails>
};

export const calculateSubTotal = (
    currentCart: Cart | undefined,
    menuRoot: MenuRoot | undefined,
    taxSettings: TaxSettings | undefined,
    orderType: OrderType,
    userLocation: Point,
    deliveryZones: DeliveryZone[],
    storeLocation: Point,
    customServiceFees: CustomServiceFee[],
    timezone: string,
    uberDirectQuote?: number | undefined,
): SubTotalResult => {
    if (!currentCart || !menuRoot) {
        return {
            totalWithTax: 0, tax: 0, itemTotal: 0, shownFee: 0,
            calculatedCustomServiceFees: [],
            bestDeliveryZone: null
        };
    }
    // Calculate cart items total
    const cartTotalValues = calculateCartTotalPrice(currentCart, menuRoot, taxSettings, orderType, timezone);

    // Initialize result
    const result: SubTotalResult = {
        itemTotal: 0,
        tax: 0,
        totalWithTax: 0,
        shownFee: 0,
        calculatedCustomServiceFees: [],
        bestDeliveryZone: null
    };
    // Calculate totals based on tax settings
    // if (!taxSettings) {
    //     console.log('❌tax settings not found');
    //     return result;
    // }

    if (orderType === 'delivery') {
        result.bestDeliveryZone = getBestDeliveryZoneWithTaxDetails(
            { lat: userLocation.lat, lng: userLocation.lng },
            deliveryZones,
            { lat: storeLocation.lat, lng: storeLocation.lng },
            taxSettings ?? {
                salesTaxType: 'ITEMS_PRICES_ALREADY_INCLUDE_TAXES',
                taxRateForServiceFees: 0,
                taxRateForDelivery: 0,
                taxName: 'Tax',
                taxCategories: []
            },
            uberDirectQuote
        );
    }

    result.totalWithTax = fixMath.add(cartTotalValues.totalWithTax, result.bestDeliveryZone?.totalWithTax ?? 0);
    result.itemTotal = fixMath.add(cartTotalValues.itemTotal, result.bestDeliveryZone?.itemTotal ?? 0);
    result.tax = fixMath.add(cartTotalValues.tax, result.bestDeliveryZone?.tax ?? 0);
    result.shownFee = taxSettings?.salesTaxType === 'APPLY_TAX_ON_TOP_OF_PRICES' ?
        result.itemTotal : result.totalWithTax

    // override editable service fees if cart has values for the id
    for (let index = 0; index < customServiceFees.length; index++) {
        const cServiceFee = customServiceFees[index];
        if (!cServiceFee) continue;
        if (!cServiceFee.id) continue;
        if (!customServiceFees[index]!.isEditable) continue;
        const cartDefinedValues = currentCart?.editableServiceFeeRates?.[cServiceFee.id];
        if (cartDefinedValues) {
            customServiceFees[index]!.ratePerOrder = Math.max(0, cartDefinedValues.ratePerOrder ?? 0);
            customServiceFees[index]!.feePerOrder = Math.max(0, cartDefinedValues.feePerOrder ?? 0);
        } else {
            if (!currentCart?.editableServiceFeeRates) {
                currentCart.editableServiceFeeRates = {};
            }
            if (!currentCart?.editableServiceFeeRates?.[cServiceFee.id]) {
                currentCart.editableServiceFeeRates[cServiceFee.id] = {}
                if (cServiceFee.ratePerOrder) {
                    currentCart.editableServiceFeeRates[cServiceFee.id]!.ratePerOrder = cServiceFee.ratePerOrder;
                }
                if (cServiceFee.feePerOrder) {
                    currentCart.editableServiceFeeRates[cServiceFee.id]!.feePerOrder = cServiceFee.feePerOrder;
                }
            }
        }
    }
    // filter custom service fees by order type
    const filteredCustomServiceFees = customServiceFees.filter(fee => {
        return fee.appliedOrderTypesArray === undefined || fee.appliedOrderTypesArray.includes(orderType);
    });
    return calculateCustomServiceFees(result, taxSettings, filteredCustomServiceFees);

};

export const calculateCustomServiceFees = (
    result: SubTotalResult,
    taxSettings: TaxSettings | undefined,
    customServiceFees: CustomServiceFee[],
): SubTotalResult => {
    // Calculate service fees
    for (const fee of customServiceFees) {
        if (!fee.id) continue;

        const feeBase = taxSettings?.salesTaxType === 'APPLY_TAX_ON_TOP_OF_PRICES'
            ? result.itemTotal
            : result.totalWithTax;
        const feeAmount = fixMath.add(exclusiveRate(feeBase, fee.ratePerOrder ?? 0), fee.feePerOrder ?? 0);

        const tax = calculateFeeTax(
            feeAmount,
            taxSettings?.salesTaxType ?? 'ITEMS_PRICES_ALREADY_INCLUDE_TAXES',
            fee.overrideServiceFeeTaxRate === undefined ? taxSettings?.taxRateForServiceFees ?? 0 : fee.overrideServiceFeeTaxRate ?? 0
        );

        const totalWithTax = taxSettings?.salesTaxType === 'APPLY_TAX_ON_TOP_OF_PRICES' ?
            fixMath.add(feeAmount, tax) : feeAmount;

        const itemTotal = taxSettings?.salesTaxType === 'APPLY_TAX_ON_TOP_OF_PRICES' ?
            feeAmount : fixMath.subtract(feeAmount, tax);


        result.calculatedCustomServiceFees.push({
            id: fee.id,
            isEditable: fee.isEditable ?? false,
            name: fee.name ?? 'Service fee',
            itemTotal,
            tax: tax,
            totalWithTax,
            shownFee: taxSettings?.salesTaxType === 'APPLY_TAX_ON_TOP_OF_PRICES' ?
                itemTotal : totalWithTax
        });
    }

    // Add service fees to totals
    result.totalWithTax = fixMath.add(result.totalWithTax, result.calculatedCustomServiceFees.reduce(
        (sum, fee) => fixMath.add(sum, fee.totalWithTax), 0
    ));

    result.tax = fixMath.add(result.tax, result.calculatedCustomServiceFees.reduce(
        (sum, fee) => fixMath.add(sum, fee.tax), 0
    ));

    result.itemTotal = fixMath.add(result.itemTotal, result.calculatedCustomServiceFees.reduce(
        (sum, fee) => fixMath.add(sum, fee.itemTotal), 0
    ));

    result.shownFee = fixMath.add(result.shownFee, result.calculatedCustomServiceFees.reduce(
        (sum, fee) => fixMath.add(sum, fee.shownFee), 0
    ));

    result.calculatedCustomServiceFees = result.calculatedCustomServiceFees.map(fee => ({
        ...fee,
        itemTotal: (fee.itemTotal),
        tax: (fee.tax),
        totalWithTax: (fee.totalWithTax),
        shownFee: (fee.shownFee)
    }));

    return result;
}